import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SelectField from "../../../../components/ui/SelectField";
import DragIcon from "./DragIcon";
import DeleteIcon from "./DeleteIcon";
import ReportSettingAction from "../../../../actions/ReportSettingAction";

interface OutlineTabContentProps {
  tableColumns: { key: string; title: string }[];
  visibleTableColumns: { key: string; title: string }[];
}

const OutlineTabContent = ({ tableColumns, visibleTableColumns }: OutlineTabContentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const reportData = useSelector((state: any) => state.reportSetting.get("data"));
  const columns = visibleTableColumns.map((item) => item.key);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Get columns that are not yet selected
  const hideTableColumns = tableColumns.filter((tableColumn) => !columns.includes(tableColumn.key));

  const setSelectedColumns = (newCols: string[]) => {
    const next = { ...reportData.toJS(), selectedColumns: newCols };
    dispatch(ReportSettingAction.settingDataReport(next));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget.outerHTML);
    (e.currentTarget as HTMLElement).style.opacity = "0.5";
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...columns];
    const draggedItem = newColumns[draggedIndex];

    // Remove dragged item from its original position
    newColumns.splice(draggedIndex, 1);

    // Insert dragged item at new position
    newColumns.splice(dropIndex, 0, draggedItem);

    setSelectedColumns(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <>
      {/* Add Column Dropdown */}
      {hideTableColumns.length > 0 && (
        <SelectField
          label={t("Add column")}
          value={null as any}
          onChange={(opt: any) => {
            const val = opt?.value || "";
            if (val) setSelectedColumns([...columns, val]);
          }}
          options={hideTableColumns.map((item) => ({ value: item.key, label: item.title }))}
          placeholder={t("Select column to add...")}
        />
      )}

      {/* Column List */}
      <div className="space-y-2">
        {visibleTableColumns.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            {t("No columns selected. Use the dropdown above to add columns.")}
          </div>
        ) : (
          visibleTableColumns.map((item, index) => (
            <div
              key={item.key}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`bg-gray-100 flex items-center rounded-lg cursor-move transition-all duration-200 ${
                draggedIndex === index ? "opacity-50" : ""
              } ${
                dragOverIndex === index && draggedIndex !== index
                  ? "bg-blue-100 border-2 border-blue-300"
                  : ""
              }`}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <DragIcon size={14} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-500">{item.title}</span>
              </div>
              <div className="flex-1"></div>
              <button
                onClick={() => setSelectedColumns((columns || []).filter((c) => c !== item.key))}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <DeleteIcon size={14} className="text-gray-500" />
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default OutlineTabContent;
